/**
 * ResultsHubPage — "Your MarrsingClause® Results"
 *
 * Permanent, token-gated access point for Reveal, Alignment Intelligence™,
 * and Certificate. Designed as a private space for clarity, not a dashboard.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowRight,
  Download,
  Sparkles,
  FileText,
  Eye,
  ShieldCheck,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import GuidedConversationModal from '../components/GuidedConversationModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric', month: 'long', day: 'numeric',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

// ── Pure UI pieces ──────────────────────────────────────────────────────────
const Divider = () => (
  <div className="h-px w-full bg-[#EFEDE6]" aria-hidden />
);

const Reassurance = ({ children }) => (
  <p className="flex items-center gap-2 text-xs text-[#888888]">
    <ShieldCheck className="h-3.5 w-3.5 text-[#888888]" aria-hidden />
    <span>{children}</span>
  </p>
);

// Loading skeleton — no layout shift on hydration
const ResultsHubSkeleton = () => (
  <div
    className="min-h-screen bg-[#FAFAF8] text-[#111111]"
    data-testid="results-hub-skeleton"
  >
    <div className="mx-auto w-full max-w-[960px] px-4 py-10 sm:px-6 md:py-16">
      <div className="space-y-4">
        <div className="h-4 w-28 rounded-full bg-[#EFEDE6]" />
        <div className="h-10 w-3/4 rounded-lg bg-[#EFEDE6]" />
        <div className="h-4 w-1/2 rounded-full bg-[#EFEDE6]" />
      </div>
      <div className="mt-12 h-48 w-full rounded-xl bg-[#F4F3EE]" />
      <div className="mt-8 grid gap-4 sm:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-32 rounded-xl bg-[#F4F3EE]" />
        ))}
      </div>
    </div>
  </div>
);

const Header = ({ data }) => (
  <header className="space-y-5" data-testid="results-header">
    <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-[#888888]">
      Your Private Results
    </p>
    <h1
      className="font-serif text-[34px] leading-[1.15] tracking-tight text-[#111111] sm:text-[44px]"
      style={{ fontFamily: "'Cormorant Garamond', 'Playfair Display', Georgia, serif" }}
    >
      Your MarrsingClause® Results
    </h1>
    <p className="max-w-xl text-base leading-7 text-[#666666] sm:text-lg">
      Your clarity. Your alignment. Your next step.
    </p>

    <div className="space-y-1 pt-2 text-sm text-[#666666]">
      <p
        className="font-serif text-[20px] text-[#111111]"
        style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
        data-testid="results-couple-names"
      >
        {data.partner_a_name} <span className="text-[#A09888]">&amp;</span>{' '}
        {data.partner_b_name}
      </p>
      <p className="text-[13px] text-[#888888]">
        Completed {formatDate(data.date_of_signing)}
        {data.certificate_id ? (
          <>
            <span className="mx-2 text-[#D0CCC0]">·</span>
            Certificate ID: <span className="tracking-wide">{data.certificate_id}</span>
          </>
        ) : null}
      </p>
    </div>

    <Reassurance>Your results are securely saved and available anytime.</Reassurance>
  </header>
);

// "Our Next Conversation" widget — one calm, guiding prompt driven by
// Alignment Intelligence output. Placed directly under the Header.
const NextConversationCard = ({ data, onStartConversation }) => {
  const nc = data.next_conversation;
  if (!nc || !nc.prompt) return null;

  return (
    <section
      className="rounded-xl border border-[#E5E2D8] bg-[#F4F3EE] px-6 py-6 sm:px-8 sm:py-7"
      data-testid="next-conversation-card"
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="max-w-2xl">
          <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-[#888888]">
            Our Next Conversation
          </p>
          <p
            className="mt-2 font-serif text-[20px] leading-[1.45] text-[#111111] sm:text-[22px]"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
            data-testid="next-conversation-prompt"
          >
            {nc.prompt}
          </p>
          {nc.category ? (
            <p className="mt-2 text-xs text-[#888888]">
              Inspired by your {nc.category.toLowerCase()} responses.
            </p>
          ) : null}
        </div>
        <div className="shrink-0">
          <button
            onClick={onStartConversation}
            className="inline-flex items-center gap-2 rounded-full bg-mc-blue-deep px-5 py-2.5 text-sm font-medium text-white transition-all hover:bg-mc-blue-dark active:scale-[0.99]"
            data-testid="next-conversation-cta"
          >
            Start This Conversation
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </section>
  );
};

const ScoreHero = ({ data }) => {
  const score = data.overall_score ?? 0;
  const circumference = 2 * Math.PI * 54;
  const offset = circumference - (score / 100) * circumference;

  return (
    <section
      className="rounded-xl border border-[#E5E2D8] bg-white px-6 py-10 sm:px-12 sm:py-14"
      style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}
      data-testid="score-hero"
    >
      <div className="flex flex-col items-center text-center">
        <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-[#888888]">
          Marrsing Alignment Score™
        </p>

        <div className="relative mt-6 flex h-[140px] w-[140px] items-center justify-center">
          <svg className="h-full w-full -rotate-90" viewBox="0 0 120 120" aria-hidden>
            <circle cx="60" cy="60" r="54" fill="none" stroke="#EFEDE6" strokeWidth="4" />
            <circle
              cx="60" cy="60" r="54" fill="none" stroke="#111111" strokeWidth="4"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              style={{ transition: 'stroke-dashoffset 700ms ease-out' }}
            />
          </svg>
          <span
            className="absolute font-serif text-[56px] leading-none text-[#111111]"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
            data-testid="overall-score-value"
          >
            {score}
          </span>
        </div>

        <p className="mt-5 text-sm uppercase tracking-[0.18em] text-[#666666]" data-testid="score-band">
          {data.score_band}
        </p>

        <div className="mt-6 max-w-md">
          <p
            className="font-serif text-[22px] leading-snug text-[#111111]"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
            data-testid="alignment-type"
          >
            {data.alignment_type}
          </p>
          <p className="mt-2 text-sm leading-6 text-[#666666]">
            {data.alignment_type_descriptor}
          </p>
        </div>
      </div>
    </section>
  );
};

const ActionBar = ({ data, onNavigate }) => (
  <section
    className="grid gap-3 sm:grid-cols-3"
    data-testid="primary-action-bar"
  >
    <button
      onClick={() => onNavigate(data.reveal_url)}
      data-testid="action-view-reveal"
      className="group flex min-h-[56px] items-center justify-center gap-2 rounded-lg bg-mc-blue-deep px-5 py-4 text-sm font-medium text-white transition-all hover:bg-mc-blue-dark active:scale-[0.99]"
    >
      <Eye className="h-4 w-4" />
      View Your Reveal
      <ArrowRight className="h-4 w-4 opacity-0 transition-opacity group-hover:opacity-100" />
    </button>
    <button
      onClick={() => onNavigate(data.intelligence_url)}
      data-testid="action-explore-intelligence"
      className="group flex min-h-[56px] items-center justify-center gap-2 rounded-lg bg-mc-blue-deep px-5 py-4 text-sm font-medium text-white transition-all hover:bg-mc-blue-dark active:scale-[0.99]"
    >
      <Sparkles className="h-4 w-4" />
      Explore Alignment Intelligence™
      <ArrowRight className="h-4 w-4 opacity-0 transition-opacity group-hover:opacity-100" />
    </button>
    <button
      onClick={() => onNavigate(data.certificate_url)}
      data-testid="action-download-certificate"
      className="group flex min-h-[56px] items-center justify-center gap-2 rounded-lg border border-[#111111] bg-white px-5 py-4 text-sm font-medium text-[#111111] transition-all hover:bg-[#F4F3EE] active:scale-[0.99]"
    >
      <Download className="h-4 w-4" />
      Download Certificate
    </button>
  </section>
);

const ModuleCard = ({ eyebrow, title, copy, items, cta, onClick, testid }) => (
  <article
    className="flex h-full flex-col justify-between rounded-xl border border-[#E5E2D8] bg-white p-6 transition-all hover:border-[#A09888]"
    style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}
    data-testid={testid}
  >
    <div>
      <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-[#888888]">
        {eyebrow}
      </p>
      <h3
        className="mt-3 font-serif text-[22px] leading-snug text-[#111111]"
        style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
      >
        {title}
      </h3>
      <p className="mt-2 text-sm leading-6 text-[#666666]">{copy}</p>
      {items && items.length > 0 && (
        <ul className="mt-4 space-y-1.5 text-[13px] text-[#666666]">
          {items.map((t) => (
            <li key={t} className="flex items-start gap-2">
              <span className="mt-1.5 h-1 w-1 rounded-full bg-[#A09888]" />
              <span>{t}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
    <button
      onClick={onClick}
      className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-[#111111] underline-offset-4 hover:underline"
      data-testid={`${testid}-cta`}
    >
      {cta}
      <ArrowRight className="h-4 w-4" />
    </button>
  </article>
);

const ModuleGrid = ({ data, onNavigate }) => (
  <section
    className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3"
    data-testid="module-grid"
  >
    <ModuleCard
      testid="module-reveal"
      eyebrow="Module 01"
      title="Your Alignment Reveal"
      copy="See where you naturally align—and where clarity can be created."
      cta="Open Reveal"
      onClick={() => onNavigate(data.reveal_url)}
    />
    <ModuleCard
      testid="module-intelligence"
      eyebrow="Module 02"
      title="Marrsing Alignment Intelligence™"
      copy="AI-assisted insights designed to help you understand your patterns, strengths, and opportunities for deeper clarity."
      items={['Alignment Insight', 'Growth Signals', 'Recommended Next Step']}
      cta="Explore Your Insights"
      onClick={() => onNavigate(data.intelligence_url)}
    />
    <ModuleCard
      testid="module-certificate"
      eyebrow="Module 03"
      title="Your MarrsingClause® Certificate"
      copy="A reflection of your alignment, clarity, and intentional commitment."
      cta="View Certificate"
      onClick={() => onNavigate(data.certificate_url)}
    />
  </section>
);

const NextStep = ({ data, onStartConversation, onNavigate }) => {
  const text = data.recommended_next_step;
  if (!text) return null;
  return (
    <section
      className="rounded-xl bg-[#F4F3EE] p-6 sm:p-10"
      data-testid="next-step-card"
    >
      <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-[#888888]">
        Your Next Step
      </p>
      <h3
        className="mt-3 font-serif text-[26px] leading-snug text-[#111111] sm:text-[30px]"
        style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
      >
        Create Your First Agreement
      </h3>
      <p className="mt-3 max-w-2xl text-[15px] leading-7 text-[#444444]">
        {text}
      </p>
      <button
        onClick={onStartConversation}
        className="mt-6 inline-flex items-center gap-2 rounded-full bg-mc-blue-deep px-6 py-3 text-sm font-medium text-white transition-all hover:bg-mc-blue-dark"
        data-testid="next-step-cta"
      >
        Start This Conversation
        <ArrowRight className="h-4 w-4" />
      </button>
    </section>
  );
};

const CATEGORY_LABELS = {
  core_values: 'Core Values',
  communication_style: 'Communication Style',
  conflict_resolution: 'Conflict Resolution',
  emotional_needs: 'Emotional Needs',
  life_vision: 'Life Vision',
};

const CategorySnapshot = ({ data }) => {
  const snapshot = data.category_snapshot || {};
  const keys = Object.keys(CATEGORY_LABELS).filter((k) => snapshot[k]);
  if (keys.length === 0) return null;
  return (
    <section data-testid="category-snapshot">
      <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-[#888888]">
        Category Interpretation Snapshot
      </p>
      <h3
        className="mt-3 font-serif text-[26px] leading-snug text-[#111111]"
        style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
      >
        How alignment shows up across five dimensions
      </h3>
      <div className="mt-6 divide-y divide-[#EFEDE6] border-y border-[#EFEDE6]">
        {keys.map((k) => (
          <div key={k} className="flex flex-col gap-2 py-5 sm:flex-row sm:gap-8">
            <p className="sm:w-52 text-sm font-medium text-[#111111]">
              {CATEGORY_LABELS[k]}
            </p>
            <p className="flex-1 text-sm leading-7 text-[#555555]">
              {snapshot[k]}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
};

// "Saved Agreements" — captured insights from guided conversations
const SavedAgreements = ({ data }) => {
  const steps = (data.saved_steps || []).filter((s) => (s.agreement || s.insight));
  if (steps.length === 0) return null;
  return (
    <section data-testid="saved-agreements">
      <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-[#888888]">
        Saved Agreements
      </p>
      <h3
        className="mt-3 font-serif text-[26px] leading-snug text-[#111111] sm:text-[28px]"
        style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
      >
        What you have chosen to carry forward
      </h3>
      <div className="mt-6 space-y-3">
        {steps.map((s) => (
          <div
            key={s.saved_step_id}
            className="rounded-xl border border-[#E5E2D8] bg-white p-5"
            data-testid={`saved-step-${s.saved_step_id}`}
          >
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#6FC7F2]">
              {s.category || 'Reflection'}
            </p>
            {s.agreement ? (
              <p
                className="mt-2 font-serif text-[18px] leading-snug text-[#111111]"
                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
              >
                “{s.agreement}”
              </p>
            ) : null}
            {s.insight ? (
              <p className="mt-2 text-sm leading-6 text-[#555555]">
                {s.insight}
              </p>
            ) : null}
          </div>
        ))}
      </div>
    </section>
  );
};

const JourneyContinues = ({ data, onNavigate }) => (
  <section
    className="rounded-xl border border-[#E5E2D8] bg-white p-6 sm:p-10"
    data-testid="journey-continues"
  >
    <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-[#6FC7F2]">
      A Living Experience
    </p>
    <h3
      className="mt-3 font-serif text-[28px] leading-snug text-[#111111] sm:text-[34px]"
      style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
    >
      Your Marrsing Journey Continues
    </h3>
    <p className="mt-4 max-w-2xl text-[15px] leading-7 text-[#444444]">
      You&rsquo;ve taken the first step toward clarity and alignment. Soon, you&rsquo;ll be able to return here to:
    </p>
    <ul className="mt-4 space-y-2.5 text-[15px] leading-6 text-[#444444]">
      <li className="flex items-start gap-3">
        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#6FC7F2]" />
        <span>Continue meaningful conversations</span>
      </li>
      <li className="flex items-start gap-3">
        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#6FC7F2]" />
        <span>Track your evolving alignment</span>
      </li>
      <li className="flex items-start gap-3">
        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#6FC7F2]" />
        <span>Build lasting relationship rituals</span>
      </li>
    </ul>
    <p
      className="mt-6 font-serif text-[20px] italic leading-snug text-[#666666]"
      style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
    >
      This is just the beginning.
    </p>
    <button
      onClick={() => onNavigate(data.intelligence_url)}
      className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-[#111111] underline-offset-4 hover:underline"
      data-testid="journey-continues-cta"
    >
      Explore Your Alignment Intelligence™
      <ArrowRight className="h-4 w-4" />
    </button>
  </section>
);

const TrustFooter = () => (
  <footer className="space-y-3 pt-8 text-center" data-testid="trust-footer">
    <Divider />
    <p className="pt-6 text-xs leading-6 text-[#888888]">
      Your MarrsingClause® experience is designed to support clarity, not replace communication.
    </p>
    <p className="text-xs text-[#888888]">
      All results are private and securely stored.
    </p>
  </footer>
);

// ── State screens ───────────────────────────────────────────────────────────
const StateCard = ({ icon: Icon, title, copy, cta, onCta, testid }) => (
  <div
    className="flex min-h-screen items-center justify-center bg-[#FAFAF8] px-4"
    data-testid={testid}
  >
    <div className="mx-auto w-full max-w-md rounded-xl border border-[#E5E2D8] bg-white p-8 text-center">
      {Icon && <Icon className="mx-auto h-8 w-8 text-[#666666]" />}
      <h2
        className="mt-4 font-serif text-[24px] text-[#111111]"
        style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
      >
        {title}
      </h2>
      <p className="mt-3 whitespace-pre-line text-sm leading-6 text-[#666666]">{copy}</p>
      {cta && (
        <button
          onClick={onCta}
          className="mt-6 inline-flex items-center gap-1.5 rounded-full bg-mc-blue-deep px-5 py-2.5 text-sm font-medium text-white transition-all hover:bg-mc-blue-dark"
          data-testid="state-cta"
        >
          {cta}
        </button>
      )}
    </div>
  </div>
);

// ── Page ────────────────────────────────────────────────────────────────────
export default function ResultsHubPage() {
  const navigate = useNavigate();
  const { relationshipId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || sessionStorage.getItem('reveal_token') || '';

  const [data, setData] = useState(null);
  const [state, setState] = useState('loading'); // loading | ok | not_ready | recovery_pending | recovery_failed | error
  const [conversationOpen, setConversationOpen] = useState(false);

  // ── Soft token recovery ────────────────────────────────────────────────
  // Per product brief: "DO NOT use strict one-time links. DO use time-based
  // + auto-recovery links." When the URL token is missing/invalid we silently
  // request a fresh token from the backend and retry — no hard error screen.
  const attemptTokenRecovery = useCallback(async () => {
    if (!relationshipId) {
      setState('error');
      return;
    }
    try {
      const r = await axios.post(`${API}/v2/results/${relationshipId}/refresh-token`);
      const newToken = r.data?.token;
      if (!newToken) throw new Error('no token returned');
      sessionStorage.setItem('reveal_token', newToken);
      // Update URL in place (no history entry) so future shares carry the
      // working token and refreshes don't loop through the recovery state.
      const url = new URL(window.location.href);
      url.searchParams.set('token', newToken);
      window.history.replaceState({}, '', url.toString());
      // Hard reload pulls the fresh token through the normal load path.
      window.location.replace(url.toString());
    } catch (e) {
      setState('recovery_failed');
    }
  }, [relationshipId]);

  useEffect(() => {
    // Persist so other pages (Certificate, AI, Reveal) can re-use
    if (relationshipId) sessionStorage.setItem('relationship_id', relationshipId);
    if (token) sessionStorage.setItem('reveal_token', token);

    let mounted = true;
    const load = async () => {
      if (!relationshipId) {
        setState('error');
        return;
      }
      if (!token) {
        // No token at all → kick off soft recovery immediately
        setState('recovery_pending');
        attemptTokenRecovery();
        return;
      }
      try {
        const r = await axios.get(`${API}/v2/results/${relationshipId}`, {
          params: { token },
        });
        if (!mounted) return;
        setData(r.data);
        setState('ok');
        // Auto-open guided conversation if email link asked for it
        if (searchParams.get('openConversation') === 'true') {
          setTimeout(() => setConversationOpen(true), 350);
        }
      } catch (e) {
        if (!mounted) return;
        const status = e.response?.status;
        if (status === 401 || status === 403) {
          // Soft-expired token → trigger silent recovery, show welcome-back UI
          setState('recovery_pending');
          attemptTokenRecovery();
        } else if (status === 400) {
          setState('not_ready');
        } else {
          setState('error');
        }
      }
    };
    load();
    return () => { mounted = false; };
  }, [relationshipId, token, searchParams, attemptTokenRecovery]);

  const reloadData = async () => {
    if (!relationshipId || !token) return;
    try {
      const r = await axios.get(`${API}/v2/results/${relationshipId}`, { params: { token } });
      setData(r.data);
    } catch { /* keep last good state */ }
  };

  const onNavigate = (url) => {
    if (!url) return;
    if (url.startsWith('http')) {
      try {
        const u = new URL(url);
        const path = u.pathname + u.search;
        navigate(path);
        return;
      } catch { /* fall through */ }
    }
    navigate(url);
  };

  if (state === 'loading') return <ResultsHubSkeleton />;

  // Soft recovery — replaces the old hard "This link has expired" screen.
  // While the silent token refresh runs in the background we still surface
  // a calm "Welcome back" card with a single CTA in case auto-recovery
  // hasn't replaced the page yet.
  if (state === 'recovery_pending') {
    return (
      <StateCard
        icon={Sparkles}
        title="Welcome back — let's continue your journey"
        copy={"For your security, this link has refreshed.\nClick below to continue where you left off."}
        cta="Continue Your Experience"
        onCta={attemptTokenRecovery}
        testid="state-recovery-pending"
      />
    );
  }

  if (state === 'recovery_failed') {
    return (
      <StateCard
        icon={AlertCircle}
        title="We couldn't reopen your journey just now"
        copy="Please try the latest email we sent, or try again in a moment."
        cta="Try Again"
        onCta={attemptTokenRecovery}
        testid="state-recovery-failed"
      />
    );
  }

  if (state === 'not_ready') {
    return (
      <StateCard
        icon={Loader2}
        title="Your insights are being prepared"
        copy="Your partner has not completed their responses yet, or your results are still being assembled. Please check back shortly."
        testid="state-not-ready"
      />
    );
  }

  if (state === 'error' || !data) {
    return (
      <StateCard
        icon={AlertCircle}
        title="Something didn't load as expected"
        copy="We couldn't load your results right now."
        cta="Retry"
        onCta={() => window.location.reload()}
        testid="state-error"
      />
    );
  }

  return (
    <div
      className="min-h-screen bg-[#FAFAF8] text-[#111111]"
      data-testid="results-hub-page"
    >
      <div className="mx-auto w-full max-w-[960px] space-y-14 px-4 py-10 sm:px-6 md:py-16">
        <Header data={data} />
        <NextConversationCard
          data={data}
          onStartConversation={() => setConversationOpen(true)}
        />
        <ScoreHero data={data} />
        <ActionBar data={data} onNavigate={onNavigate} />
        <Divider />
        <div className="space-y-6">
          <div>
            <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-[#888888]">
              Your Experience
            </p>
            <h2
              className="mt-3 font-serif text-[28px] leading-snug text-[#111111] sm:text-[32px]"
              style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
            >
              Three places to return to, whenever you need clarity
            </h2>
          </div>
          <ModuleGrid data={data} onNavigate={onNavigate} />
        </div>
        <NextStep
          data={data}
          onStartConversation={() => setConversationOpen(true)}
          onNavigate={onNavigate}
        />
        <SavedAgreements data={data} />
        <CategorySnapshot data={data} />
        <JourneyContinues data={data} onNavigate={onNavigate} />
        <TrustFooter />
      </div>

      <GuidedConversationModal
        open={conversationOpen}
        onClose={() => setConversationOpen(false)}
        relationshipId={relationshipId}
        token={token}
        tier={data.tier}
        categoryIntelligence={data.category_intelligence || []}
        onSaved={reloadData}
      />
    </div>
  );
}

export { ResultsHubSkeleton };
